import { TextFieldModule } from '@angular/cdk/text-field'
import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { PipesModule } from '@core/pipes/pipes.module'
import { TranslateProductMeasurePipe } from '@core/pipes/translate-product-measure/translate-product-measure.pipe'
import {
  PalantirAccordionComponent,
  PalantirButtonComponent,
  PalantirCheckboxComponent,
  PalantirContentComponent,
  PalantirInputComponent,
  PalantirModalComponent,
  PalantirModalRef,
  PalantirModule,
  PalantirSelectOption,
  PalantirTextComponent,
  PalantirToolbarComponent,
  PalantirTooltipModule
} from '@fieldcontrol/palantir'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { WarrantyStatuses } from './filter-modal.types'
import { dateRangeValidator } from './filter.validator'
import { AssetsTreeFilterService } from '../../../../services/assets-tree-filter.service'

@Component({
  selector: 'app-filters-modal',
  templateUrl: './filters-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NgScrollbarModule,
    ReactiveFormsModule,
    PalantirModalComponent,
    PalantirContentComponent,
    PalantirAccordionComponent,
    PalantirCheckboxComponent,
    PalantirInputComponent,
    PalantirButtonComponent,
    PalantirToolbarComponent,
    PalantirModule,
    PalantirTooltipModule,
    PalantirTextComponent,
    PipesModule,
    TextFieldModule,
    TranslateModule
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ]
})
export class AssetsTreeFiltersModalComponent implements OnInit {
  public readonly translateProductMeasurePipe = inject(TranslateProductMeasurePipe)
  private readonly palantirModalRef = inject(PalantirModalRef)
  public readonly assetsTreeFilterService = inject(AssetsTreeFilterService)
  private readonly formBuilder = inject(FormBuilder)
  private readonly changeDetectorRef = inject(ChangeDetectorRef)
  public isAllSelected = signal(false)
  public WarrantyStatuses = WarrantyStatuses
  public warrantyStatuses = [
    WarrantyStatuses.ACTIVE,
    WarrantyStatuses.EXPIRED,
    WarrantyStatuses.EXTENDED,
    WarrantyStatuses.FUTURE
  ]
  public warrantyStatusIn = signal<WarrantyStatuses[]>([])
  public assetsTreeFilterForm: FormGroup

  public constructor() {
    this.assetsTreeFilterForm = this.formBuilder.group({
      warrantyStartDateLte: null,
      warrantyEndDateGte: null,
      warrantyStatusIn: null
    }, { validators: dateRangeValidator() })

  }
  public ngOnInit(): void {
    this.assetsTreeFilterForm.patchValue({
      warrantyStartDateLte: this.assetsTreeFilterService.form.get('warrantyStartDateLte')?.value,
      warrantyEndDateGte: this.assetsTreeFilterService.form.get('warrantyEndDateGte')?.value,
      warrantyStatusIn: this.assetsTreeFilterService.form.get('warrantyStatusIn')?.value
    })

    const warrantyStatusIn = this.assetsTreeFilterService.form.get('warrantyStatusIn')?.value
    this.warrantyStatusIn.set(warrantyStatusIn ? warrantyStatusIn : [])

    this.filtersFormValueChanges()
  }

  public filtersFormValueChanges(): void {
    this.assetsTreeFilterForm.valueChanges.subscribe(async () => {
      const status = this.warrantyStatusIn()
      this.assetsTreeFilterForm.get('warrantyStatusIn')?.setValue(status.length ? status : null, { emitEvent: false })
      this.assetsTreeFilterService.isFiltering.set(true)

      if (this.assetsTreeFilterForm.valid) {
        this.assetsTreeFilterService.form.patchValue(this.assetsTreeFilterForm.value)
      }
    })
  }

  public toggleAllSelection(): void {
    this.isAllSelected.set(!this.isAllSelected())
    this.warrantyStatusIn?.set(this.isAllSelected() ? this.warrantyStatuses : [])
    this.assetsTreeFilterForm.get('warrantyStatusIn')?.setValue(this.warrantyStatusIn())
  }

  public toggleSelection(status: WarrantyStatuses): void {
    if (this.warrantyStatusIn().includes(status)) {
      this.warrantyStatusIn.set(this.warrantyStatusIn().filter((item: string) => item !== status))
    } else {
      this.warrantyStatusIn?.set([...this.warrantyStatusIn(), status])
    }

    this.isAllSelected.set(this.warrantyStatusIn().length === this.warrantyStatuses.length)
    this.assetsTreeFilterForm.get('warrantyStatusIn')?.setValue(this.warrantyStatusIn())
  }

  public handleRadioChange($event: any, controlName: string): void {
    this.assetsTreeFilterService.form.get(controlName)?.setValue($event?.value[0])
    this.assetsTreeFilterService.form.get(controlName)?.markAsDirty()
  }

  public clearFiltersEvent(): void {
    this.isAllSelected.set(false)
    this.warrantyStatusIn.set([])
    this.assetsTreeFilterForm.reset()
    this.assetsTreeFilterForm.markAsPristine()
    this.assetsTreeFilterForm.updateValueAndValidity()
    this.changeDetectorRef.detectChanges()
    this.assetsTreeFilterService.clearFilters()
  }

  public mapper = (option: PalantirSelectOption): PalantirSelectOption => {
    return option
  }

  public trackByFn(index: number): number {
    return index
  }

  public async close(): Promise<void> {
    await this.palantirModalRef.close()
  }
}
